import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod/v4'
import { SettingsLayout, SettingsSection } from '~/settings/components/settings-layout'
import { formatDate } from '~/utils'

const UsageMetricSchema = z.object({
    id: z.number(),
    name: z.string(),
    unit: z.string(),
    description: z.string().optional(),
})
export type UsageMetric = z.infer<typeof UsageMetricSchema>

const UsageRecordSchema = z.object({
    id: z.number(),
    metric: UsageMetricSchema,
    period_start: z.coerce.date(),
    period_end: z.coerce.date(),
    quantity: z.number(),
    limit: z.number().optional(),
    usage_since: z.coerce.date(),
})
export type UsageRecord = z.infer<typeof UsageRecordSchema>

const UsageSummarySchema = z.object({
    next_refresh_date: z.coerce.date(),
    records: z.array(UsageRecordSchema),
})
export type UsageSummary = z.infer<typeof UsageSummarySchema>

async function getUsageSummary(): Promise<UsageSummary> {
    await new Promise((resolve) => setTimeout(resolve, 1000))

    const mockResponse = {
        next_refresh_date: '2025-07-30',
        records: [
            {
                id: 1,
                metric: {
                    id: 1,
                    name: 'user_prompt_credits',
                    unit: 'credits',
                    description: 'Using a premium model costs one prompt credit per use',
                },
                period_start: '2025-06-30',
                period_end: '2025-08-30',
                quantity: 119.0,
                limit: 500,
                usage_since: '2025-06-30',
            },
            {
                id: 2,
                metric: {
                    id: 2,
                    name: 'addon_prompt_credits',
                    unit: 'credits',
                    description: 'Additional prompt credits',
                },
                period_start: '2025-06-30',
                period_end: '2025-08-30',
                quantity: 0.0,
                limit: 289,
                usage_since: '2025-06-30',
            },
        ],
    }

    const result = UsageSummarySchema.parse(mockResponse)
    return result
}

const useUsageSummary = () => {
    return useQuery({
        queryKey: ['usage-summary'],
        queryFn: async () => {
            const summary = await getUsageSummary()
            return summary
        },
    })
}

function UsageMetricCardSkeleton() {
    return (
        <Stack spacing={2}>
            <Box>
                <Skeleton variant="text" width={200} height={32} sx={{ mb: 1 }} />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                    }}
                >
                    <Box>
                        <Skeleton variant="text" width={150} height={24} sx={{ mb: 0.5 }} />
                        <Skeleton variant="text" width={250} height={20} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', flexDirection: 'column' }}>
                        <Skeleton variant="text" width={100} height={24} sx={{ mb: 0.5 }} />
                        <Skeleton variant="text" width={150} height={20} />
                    </Box>
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Skeleton
                        variant="rectangular"
                        width="100%"
                        height={8}
                        sx={{ borderRadius: 4 }}
                    />
                </Box>
            </Box>
        </Stack>
    )
}

function UsageSummarySkeleton() {
    return (
        <SettingsSection header="Usage Summary">
            <Stack spacing={4}>
                <UsageMetricCardSkeleton />
                <Divider sx={{ mt: 4 }} />
                <UsageMetricCardSkeleton />
            </Stack>
        </SettingsSection>
    )
}

type UsageMetricCardProps = {
    metricName: string
    quantity: number
    usageSince: Date
    limit?: number
    description?: string
}

function UsageMetricCard({
    metricName,
    quantity,
    limit,
    usageSince,
    description,
}: UsageMetricCardProps) {
    const remaining = limit ? limit - quantity : 0
    const usagePercentage = limit ? (quantity / limit) * 100 : 0

    const getMetricTitle = () => {
        switch (metricName) {
            case 'user_prompt_credits':
                return gettext('User Prompt credits')
            case 'addon_prompt_credits':
                return gettext('Add-on prompt credits')
            default:
                return metricName
        }
    }

    return (
        <Stack spacing={2}>
            <Box>
                <Typography gutterBottom variant="h6">
                    {getMetricTitle()}
                </Typography>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'baseline',
                    }}
                >
                    <Box>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {interpolate(gettext('%s / %s used'), [quantity.toFixed(), limit])}
                        </Typography>
                        {description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                {description}
                            </Typography>
                        )}
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {interpolate(gettext('%s left'), [remaining.toFixed(2)])}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {interpolate(gettext('Usage since %s'), [
                                formatDate(usageSince, { format: 'MMM D, YYYY' }),
                            ])}
                        </Typography>
                    </Box>
                </Box>
                {limit && (
                    <Box sx={{ mt: 2 }}>
                        <LinearProgress
                            variant="determinate"
                            value={Math.min(usagePercentage, 100)}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    backgroundColor:
                                        usagePercentage > 90 ? 'error.main' : 'primary.main',
                                },
                            }}
                        />
                    </Box>
                )}
            </Box>
        </Stack>
    )
}

function UsageSummarySection() {
    const { data, isLoading } = useUsageSummary()

    if (isLoading) {
        return <UsageSummarySkeleton />
    }

    if (!data) {
        return null
    }

    const getSubHeader = () => {
        return interpolate(
            gettext('Next plan refresh is in <%(tag)s>25 days</%(tag)s> on %(nextRefreshDate)s.'),
            {
                tag: 'strong',
                nextRefreshDate: formatDate(data.next_refresh_date, { format: 'MMM D, YYYY' }),
            },
            true
        )
    }

    return (
        <SettingsSection header={gettext('Usage Summary')} subHeader={getSubHeader()}>
            <Stack spacing={4}>
                {data.records.map((record, index) => (
                    <Box key={record.id}>
                        <UsageMetricCard
                            metricName={record.metric.name}
                            quantity={record.quantity}
                            limit={record.limit}
                            usageSince={record.usage_since}
                            description={record.metric.description}
                        />
                        {index < data.records.length - 1 && <Divider sx={{ mt: 4 }} />}
                    </Box>
                ))}
            </Stack>
        </SettingsSection>
    )
}

export function SettingsUsage() {
    return (
        <SettingsLayout>
            <UsageSummarySection />
        </SettingsLayout>
    )
}
